import React, { useEffect, useRef, useState } from "react";
import { graphql, Link, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import styled from "styled-components";
import sr from "@utils/sr";
import { srConfig } from "@config";
import { Icon } from "@components/icons";
import { usePrefersReducedMotion } from "@hooks";

const StyledProjectsPage = styled.main`
  width: 100%;
`;

const HeroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 170px clamp(28px, 6vw, 80px) 84px;

  .eyebrow {
    margin: 0 0 18px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    margin: 0 0 22px;
    color: var(--text-primary);
    font-size: clamp(42px, 6vw, 82px);
    line-height: 0.98;
    letter-spacing: -0.05em;
    max-width: 920px;
  }

  .lead {
    margin: 0;
    max-width: 820px;
    color: var(--text-secondary);
    font-size: clamp(18px, 2vw, 23px);
    line-height: 1.8;
  }

  .lead strong {
    color: var(--text-primary);
    font-weight: 600;
  }
`;

const IntroSection = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 34px;

  .row {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 20px;
    align-items: end;

    @media (max-width: 760px) {
      grid-template-columns: 1fr;
    }
  }

  p {
    margin: 0;
    max-width: 760px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.9;
  }

  .archiveLink {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 44px;
    padding: 0 16px;
    border-radius: 999px;
    text-decoration: none;
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-primary);
    background: rgba(255, 255, 255, 0.52);
    border: 1px solid var(--border-light);
    transition: var(--transition);
  }

  .archiveLink:hover {
    color: var(--text-primary);
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.76);
    transform: translateY(-1px);
  }
`;

const SectionBlock = styled.section`
  max-width: 1260px;
  margin: 0 auto;
  padding: 0 clamp(28px, 6vw, 80px) 96px;

  .eyebrow {
    margin: 0 0 12px;
    color: var(--text-muted);
    font-family: var(--font-mono);
    font-size: 13px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h2 {
    margin: 0;
    color: var(--text-primary);
    font-size: clamp(34px, 4.4vw, 58px);
    line-height: 1.06;
    letter-spacing: -0.045em;
    max-width: 820px;
  }

  .sub {
    margin: 16px 0 0;
    max-width: 760px;
    color: var(--text-secondary);
    font-size: 17px;
    line-height: 1.9;
  }
`;

const FeaturedList = styled.div`
  margin-top: 34px;
  border-top: 1px solid var(--border-light);
`;

const FeaturedProject = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.95fr);
  gap: 30px;
  align-items: center;
  padding: 34px 0;
  border-bottom: 1px solid var(--border-light);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }

  .content {
    min-width: 0;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 999px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    border: 1px solid var(--border-light);
    background: rgba(255, 255, 255, 0.28);
  }

  h3 {
    margin: 0 0 14px;
    color: var(--text-primary);
    font-size: clamp(30px, 3.2vw, 44px);
    line-height: 1.04;
    letter-spacing: -0.04em;
    max-width: 13ch;
  }

  h3 a {
    color: inherit;
    text-decoration: none;
  }

  h3 a:hover,
  h3 a:focus-visible {
    text-decoration: underline;
    text-underline-offset: 4px;
    outline: none;
  }

  .description {
    color: var(--text-secondary);
    font-size: 16px;
    line-height: 1.92;
    max-width: 62ch;
  }

  .description p {
    margin: 0 0 12px;
  }

  .description p:last-child {
    margin-bottom: 0;
  }

  .description strong {
    color: var(--text-primary);
    font-weight: 600;
  }

  .tech {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin: 18px 0 0;
    padding: 0;
    list-style: none;
  }

  .tech li {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
  }

  .links {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 18px;
  }

  .iconLink {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    color: var(--text-primary);
    border: 1px solid var(--border-light);
    background: transparent;
    transition: var(--transition);
  }

  .iconLink:hover {
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.35);
    transform: translateY(-1px);
  }

  .iconLink svg {
    width: 16px;
    height: 16px;
  }

  .imageWrap {
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid var(--border-light);
    background: rgba(255, 255, 255, 0.28);
    min-height: 100%;
  }

  .imageLink {
    display: block;
    width: 100%;
    height: 100%;
  }

  .image {
    width: 100%;
    height: 100%;
  }

  .image img {
    object-fit: cover !important;
  }
`;

const ProjectList = styled.div`
  margin-top: 34px;
  border-top: 1px solid var(--border-light);
`;

const ProjectRow = styled.article`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(180px, 0.52fr) auto;
  gap: 18px;
  align-items: start;
  padding: 24px 0;
  border-bottom: 1px solid var(--border-light);

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .titleWrap {
    min-width: 0;
  }

  .titleWrap h3 {
    margin: 0 0 8px;
    color: var(--text-primary);
    font-size: 27px;
    line-height: 1.14;
    letter-spacing: -0.03em;
  }

  .titleWrap h3 a {
    color: inherit;
    text-decoration: none;
  }

  .titleWrap h3 a:hover,
  .titleWrap h3 a:focus-visible {
    text-decoration: underline;
    text-underline-offset: 4px;
    outline: none;
  }

  .description {
    color: var(--text-secondary);
    font-size: 15px;
    line-height: 1.88;
    max-width: 64ch;
  }

  .description p {
    margin: 0;
  }

  .description strong {
    color: var(--text-primary);
    font-weight: 600;
  }

  .tech {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 12px;
    padding: 2px 0 0;
    list-style: none;
    margin: 0;
  }

  .tech li {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--text-secondary);
    white-space: nowrap;
  }

  .links {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-end;

    @media (max-width: 920px) {
      justify-content: flex-start;
    }
  }

  .iconLink {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 999px;
    color: var(--text-primary);
    border: 1px solid var(--border-light);
    background: transparent;
    transition: var(--transition);
  }

  .iconLink:hover {
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.35);
    transform: translateY(-1px);
  }

  .iconLink svg {
    width: 16px;
    height: 16px;
  }
`;

const ShowMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 28px;
`;

const ShowMoreButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 46px;
  padding: 0 20px;
  border-radius: 999px;
  font-family: var(--font-mono);
  font-size: 13px;
  cursor: pointer;
  transition: var(--transition);

  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.52);
  border: 1px solid var(--border-light);

  &:hover {
    border-color: var(--border-medium);
    background: rgba(255, 255, 255, 0.76);
    transform: translateY(-1px);
  }
`;

const OdysseyIntro = styled.div`
  margin-top: 30px;
  max-width: 760px;

  h3 {
    margin: 0 0 14px;
    color: var(--text-primary);
    font-size: clamp(30px, 3.2vw, 42px);
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: 16px;
    line-height: 1.9;
  }
`;

const OdysseyNotes = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const OdysseyNote = styled.div`
  padding: 16px 0 0;
  border-top: 1px solid var(--border-light);

  strong {
    display: block;
    margin-bottom: 8px;
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 600;
  }

  span {
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.85;
  }
`;

const MapOuter = styled.div`
  margin-top: 28px;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--border-light);
  background: rgba(255, 255, 255, 0.28);
`;

const MapInner = styled.div`
  width: 100%;
  min-height: 620px;
  position: relative;

  @media (max-width: 760px) {
    min-height: 420px;
  }

  .mapFallback {
    padding: 24px;
    color: var(--text-secondary);
    font-size: 15px;
  }

  /* Force leaflet map area to fill the wrapper */
  .map-container,
  .leaflet-container {
    width: 100%;
    height: 620px;
  }

  @media (max-width: 760px) {
    .map-container,
    .leaflet-container {
      height: 420px;
    }
  }
`;

function OdysseyMap() {
  const [ClientMap, setClientMap] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let mounted = true;

    import("./TravelMapClient")
      .then(mod => {
        if (mounted) setClientMap(() => mod.default);
      })
      .catch(() => {
        if (mounted) setLoadError(true);
      });

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <MapOuter>
      <MapInner>
        {loadError ? (
          <div className="mapFallback">Map could not be loaded.</div>
        ) : ClientMap ? (
          <ClientMap />
        ) : (
          <div className="mapFallback">Loading map…</div>
        )}
      </MapInner>
    </MapOuter>
  );
}

const ProjectsPageContent = () => {
  const data = useStaticQuery(graphql`
    query ProjectsPageQuery {
      featured: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/featured/" } }
        sort: { fields: [frontmatter___date], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              title
              cover {
                childImageSharp {
                  gatsbyImageData(
                    width: 900
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
              tech
              github
              external
            }
            html
          }
        }
      }

      projects: allMarkdownRemark(
        filter: {
          fileAbsolutePath: { regex: "/content/projects/" }
          frontmatter: { showInProjects: { ne: false } }
        }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              tech
              github
              external
            }
            html
          }
        }
      }
    }
  `);

  const prefersReducedMotion = usePrefersReducedMotion();
  const revealContainer = useRef(null);
  const [showMore, setShowMore] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion || !revealContainer.current) return;
    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  const featuredProjects = data?.featured?.edges?.filter(({ node }) => node) ?? [];
  const allProjects = data?.projects?.edges?.filter(({ node }) => node) ?? [];

  const GRID_LIMIT = 6;
  const projectsToShow = showMore ? allProjects : allProjects.slice(0, GRID_LIMIT);

  return (
    <StyledProjectsPage ref={revealContainer}>
      <HeroSection>
        <p className="eyebrow">Projects</p>
        <h1>Software, data systems, machine learning, and exploratory work.</h1>
        <p className="lead">
          A mix of <strong>production-minded systems, analytical tools, dashboards, machine learning experiments, and personal technical exploration</strong> across multiple domains.
        </p>
      </HeroSection>

      <IntroSection>
        <div className="row">
          <p>
            This page brings together selected professional and personal work. Some projects focus on
            analytics and decision systems, some on software and automation, and others on experimentation,
            exploration, and curiosity.
          </p>

          <Link className="archiveLink" to="/archive">
            View full archive
          </Link>
        </div>
      </IntroSection>

      <SectionBlock>
        <p className="eyebrow">Selected Work</p>
        <h2>Projects with stronger depth, clearer outcomes, and broader technical range.</h2>
        <p className="sub">
          A smaller set of projects that best represent how I think, build, and connect technical systems to useful results.
        </p>

        <FeaturedList>
          {featuredProjects.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { external, title, tech, github, cover } = frontmatter;
            const image = getImage(cover);
            const primaryUrl = external || github || "#";

            return (
              <FeaturedProject key={`${title}-${i}`}>
                <div className="content">
                  <div className="meta">
                    <span className="pill">Featured Project</span>
                  </div>

                  <h3>
                    <a href={primaryUrl} target="_blank" rel="noreferrer">
                      {title}
                    </a>
                  </h3>

                  <div
                    className="description"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />

                  {tech?.length ? (
                    <ul className="tech">
                      {tech.map((item, idx) => (
                        <li key={`${item}-${idx}`}>{item}</li>
                      ))}
                    </ul>
                  ) : null}

                  <div className="links">
                    {github ? (
                      <a
                        className="iconLink"
                        href={github}
                        aria-label="GitHub Link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Icon name="GitHub" />
                      </a>
                    ) : null}

                    {external ? (
                      <a
                        className="iconLink"
                        href={external}
                        aria-label="External Link"
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Icon name="External" />
                      </a>
                    ) : null}
                  </div>
                </div>

                <div className="imageWrap">
                  <a
                    className="imageLink"
                    href={primaryUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {image ? <GatsbyImage image={image} alt={title} className="image" /> : null}
                  </a>
                </div>
              </FeaturedProject>
            );
          })}
        </FeaturedList>
      </SectionBlock>

      <SectionBlock>
        <p className="eyebrow">Project Library</p>
        <h2>Broader work across domains, tools, and technical interests.</h2>
        <p className="sub">
          Additional projects covering dashboards, analysis, applications, forecasting, automation, and other technical builds.
        </p>

        <ProjectList>
          {projectsToShow.map(({ node }, i) => {
            const { frontmatter, html } = node;
            const { github, external, title, tech } = frontmatter;
            const primaryUrl = external || github || "#";

            return (
              <ProjectRow key={`${title}-${i}`}>
                <div className="titleWrap">
                  <h3>
                    <a href={primaryUrl} target="_blank" rel="noreferrer">
                      {title}
                    </a>
                  </h3>

                  <div
                    className="description"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                </div>

                <ul className="tech">
                  {tech?.map((item, idx) => (
                    <li key={`${item}-${idx}`}>{item}</li>
                  ))}
                </ul>

                <div className="links">
                  {github ? (
                    <a
                      className="iconLink"
                      href={github}
                      aria-label="GitHub Link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="GitHub" />
                    </a>
                  ) : null}

                  {external ? (
                    <a
                      className="iconLink"
                      href={external}
                      aria-label="External Link"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Icon name="External" />
                    </a>
                  ) : null}
                </div>
              </ProjectRow>
            );
          })}
        </ProjectList>

        {allProjects.length > GRID_LIMIT ? (
          <ShowMoreRow>
            <ShowMoreButton type="button" onClick={() => setShowMore(prev => !prev)}>
              Show {showMore ? "Less" : "More"}
            </ShowMoreButton>
          </ShowMoreRow>
        ) : null}
      </SectionBlock>

      <SectionBlock style={{ paddingBottom: "150px" }}>
        <p className="eyebrow">Odyssey</p>
        <h2>Movement, curiosity, and the personal side of exploration.</h2>
        <p className="sub">
          A lighter section that reflects travel history and future places I want to explore.
        </p>

        <OdysseyIntro>
          <h3>Travel history and future plans.</h3>
          <p>
            Odyssey brings in a more personal layer to the portfolio. Beyond technical work,
            I value exploration, new environments, and experiences that widen perspective.
          </p>
        </OdysseyIntro>

        <OdysseyMap />

        <OdysseyNotes>
          <OdysseyNote>
            <strong>Why include this here</strong>
            <span>
              Because projects also come from curiosity, movement, and exposure to different places and ideas.
            </span>
          </OdysseyNote>

          <OdysseyNote>
            <strong>What it represents</strong>
            <span>
              A quieter record of where I have been and where I want to go next.
            </span>
          </OdysseyNote>
        </OdysseyNotes>
      </SectionBlock>
    </StyledProjectsPage>
  );
};

export default ProjectsPageContent;